import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect } from 'react';
import { generateUrl } from '@magento/peregrine/lib/util/imageUtils';
import { useIntl } from 'react-intl';
import { Image, Button, InfoIcon } from 'src/components';
import SelectOptionsItem from '../common/selectOptionItem'; // import Tearsheet from '../common/tearsheet';

import { Form } from 'informed';
import FormError from '@magento/venia-ui/lib/components/FormError';
import Price from '@magento/venia-ui/lib/components/Price';
import { QuantityFields } from '@magento/venia-ui/lib/components/CartPage/ProductListing/quantity';
import { useProductFullDetail } from '@magento/peregrine/lib/talons/ProductFullDetail/useProductFullDetail';
import { useToasts } from '@magento/peregrine';
import DownloadsPopup from '../downloadsPopup';
var WishlistButton = React.lazy(function () {
  return import('src/overrides/venia-ui/components/Wishlist/AddToListPdpButton');
});
import classes from './availableOutletItem.module.css';
import { ProvidedRequiredArgumentsOnDirectivesRule } from 'graphql/validation/rules/ProvidedRequiredArgumentsRule'; // Correlate a GQL error message to a field. GQL could return a longer error
// string but it may contain contextual info such as product id. We can use
// parts of the string to check for which field to apply the error.

var ERROR_MESSAGE_TO_FIELD_MAPPING = {
  'The requested qty is not available': 'quantity',
  'Product that you are trying to add is not available.': 'quantity',
  "The product that was requested doesn't exist.": 'quantity'
}; // Field level error messages for rendering.

var ERROR_FIELD_TO_MESSAGE_MAPPING = {
  quantity: React.createElement("div", {
    className: 'text-red mt-4 text-center'
  }, "The requested quantity is not available.")
};
var SMALL_IMAGE_WIDTH = 400;

var extractOutletSortNumber = function extractOutletSortNumber(url) {
  var regex = /\/([^\/_]*_(\d+))/;
  var result = url.match(regex);

  if (result) {
    return parseInt(result[2]);
  } else {
    // console.log("No number preceded by underscore was found in the URL after the last '/'");
    return 1000;
  }
};

var extractPrimaryOutletImage = function extractPrimaryOutletImage(media_gallery) {
  var _media_gallery$, _media_gallery$2;

  if (media_gallery && media_gallery.length < 1) return null;
  var primaryImage = (_media_gallery$ = media_gallery[0]) === null || _media_gallery$ === void 0 ? void 0 : _media_gallery$.url;
  var minSortNumber = extractOutletSortNumber((_media_gallery$2 = media_gallery[0]) === null || _media_gallery$2 === void 0 ? void 0 : _media_gallery$2.url);
  media_gallery === null || media_gallery === void 0 ? void 0 : media_gallery.forEach(function (item) {
    var _media_gallery$3;

    var currentSortNumber = extractOutletSortNumber((_media_gallery$3 = media_gallery[0]) === null || _media_gallery$3 === void 0 ? void 0 : _media_gallery$3.url);

    if (currentSortNumber < minSortNumber) {
      minSortNumber = currentSortNumber;
      primaryImage = item === null || item === void 0 ? void 0 : item.url;
    }
  });
  return primaryImage;
};

var AvailableOutletItem = function AvailableOutletItem(props) {
  var _item$small_image, _item$small_image2, _item$small_image3, _customAttributes$fin, _customAttributes$fin2, _item$price, _item$price$regularPr, _item$price$regularPr2, _item$price_range, _item$price_range$max, _item$price_range$max2, _item$price2, _item$price2$regularP, _item$price2$regularP2, _item$price3, _item$price3$regularP, _item$price3$regularP2, _item$price_range2, _item$price_range2$ma, _item$price_range2$ma2, _item$price_range3, _item$price_range3$ma, _item$price_range3$ma2, _item$price4, _item$price4$regularP, _item$price4$regularP2, _item$price_range4, _item$price_range4$ma, _item$price_range4$ma2, _item$price_range5, _item$price_range5$ma, _item$price_range5$ma2, _item$price_range6, _item$price_range6$ma, _item$price_range6$ma2;

  var item = props.item,
      configurableOptions = props.configurableOptions,
      product = props.product,
      customAttributes = props.customAttributes,
      wishlistButtonProps = props.wishlistButtonProps,
      isWishlistDialogOpen = props.isWishlistDialogOpen,
      setModalData = props.setModalData,
      setIsWishlistDialogOpen = props.setIsWishlistDialogOpen,
      parentImage = props.parentImage;
  var talonProps = useProductFullDetail({
    product: item
  });
  var errorMessage = talonProps.errorMessage,
      handleAddToCart = talonProps.handleAddToCart,
      successAddToCartToast = talonProps.successAddToCartToast;

  var _useIntl = useIntl(),
      formatMessage = _useIntl.formatMessage; // Fill a map with field/section -> error.


  var errors = new Map();

  if (errorMessage) {
    Object.keys(ERROR_MESSAGE_TO_FIELD_MAPPING).forEach(function (key) {
      if (errorMessage.includes(key)) {
        var target = ERROR_MESSAGE_TO_FIELD_MAPPING[key];
        var message = ERROR_FIELD_TO_MESSAGE_MAPPING[target];
        errors.set(target, message);
      }
    }); // Handle cases where a user token is invalid or expired. Preferably
    // this would be handled elsewhere with an error code and not a string.

    if (errorMessage.includes('The current user cannot')) {
      errors.set('outletForm', [new Error(formatMessage({
        id: 'productFullDetail.errorToken',
        defaultMessage: 'There was a problem with your cart. Please sign in again and try adding the item once more.'
      }))]);
    } // Handle cases where a cart wasn't created properly.


    if (errorMessage.includes('Variable "$cartId" got invalid value null')) {
      errors.set('outletForm', [new Error(formatMessage({
        id: 'productFullDetail.errorCart',
        defaultMessage: 'There was a problem with your cart. Please refresh the page and try adding the item once more.'
      }))]);
    } // An unknown error should still present a readable message.


    if (!errors.size) {
      errors.set('outletForm', [new Error(formatMessage({
        id: 'productFullDetail.errorUnknown',
        defaultMessage: 'Could not add item to cart. Please check required options and try again.'
      }))]);
    }
  }

  var outletImage = item !== null && item !== void 0 && (_item$small_image = item.small_image) !== null && _item$small_image !== void 0 && _item$small_image.url && !(item !== null && item !== void 0 && (_item$small_image2 = item.small_image) !== null && _item$small_image2 !== void 0 && _item$small_image2.url.includes('placeholder')) ? item === null || item === void 0 ? void 0 : (_item$small_image3 = item.small_image) === null || _item$small_image3 === void 0 ? void 0 : _item$small_image3.url : extractPrimaryOutletImage(item === null || item === void 0 ? void 0 : item.media_gallery);
  var imgUrl = outletImage ? generateUrl(outletImage, 'image-product')(SMALL_IMAGE_WIDTH) : parentImage ? generateUrl(parentImage, 'image-product')(SMALL_IMAGE_WIDTH) : null;
  var selectOptionItems = configurableOptions === null || configurableOptions === void 0 ? void 0 : configurableOptions.map(function (configItem) {
    var _item$custom_attribut, _configItem$values;

    var customAttr = (_item$custom_attribut = item.custom_attributes) === null || _item$custom_attribut === void 0 ? void 0 : _item$custom_attribut.find(function (custom_attribute) {
      return custom_attribute.attribute_metadata.code == configItem.attribute_code;
    });

    if (!customAttr) {
      return null;
    }

    var selectedOption = (_configItem$values = configItem.values) === null || _configItem$values === void 0 ? void 0 : _configItem$values.find(function (value) {
      var _customAttr$selected_, _customAttr$selected_2;

      return value.store_label == (customAttr === null || customAttr === void 0 ? void 0 : (_customAttr$selected_ = customAttr.selected_attribute_options) === null || _customAttr$selected_ === void 0 ? void 0 : (_customAttr$selected_2 = _customAttr$selected_.attribute_option[0]) === null || _customAttr$selected_2 === void 0 ? void 0 : _customAttr$selected_2.label);
    });

    if (!selectedOption) {
      return null;
    }

    return React.createElement(SelectOptionsItem, {
      option: selectedOption,
      label: configItem.store_label,
      key: selectedOption.value_index
    });
  });

  var _useToasts = useToasts(),
      _useToasts2 = _slicedToArray(_useToasts, 2),
      addToast = _useToasts2[1].addToast;

  useEffect(function () {
    if (successAddToCartToast) {
      addToast(successAddToCartToast);
    }
  }, [successAddToCartToast]); // eslint-disable-line react-hooks/exhaustive-deps

  var wItem = {
    sku: item.sku,
    quantity: 1
  };
  return React.createElement("div", {
    className: "border border-solid border-gray flex flex-col md_flex-row sm_justify-between sm_p-10 mb-5"
  }, React.createElement("div", {
    className: classes.imageWrapper
  }, imgUrl && React.createElement(Image, {
    classes: {
      container: classes.imageContainer
    },
    alt: "".concat(item.name) + ' image',
    src: imgUrl
  }), React.createElement("div", {
    className: "flex lg_w-full py-[10px] lg_w-9/12 sm_absolute sm_bottom-0 sm_-left-10 sm_bg-[#f2f2f2]"
  }, React.createElement("div", {
    className: "hidden sm_block sm_self-center sm_px-2.5"
  }, React.createElement(InfoIcon, null)), React.createElement("div", {
    className: "text-red text-center leading-6 text-base w-full px-11 sm_text-xs sm_px-0 sm_pr-2.5 sm_text-left sm_text-black"
  }, "Image shown may not necessarily correspond to selected options"))), React.createElement("div", {
    className: "flex-1 mb-9 flex flex-col px-0 sm_mb-0 md_px-8"
  }, React.createElement("div", {
    className: classes.outletTitle
  }, item.name), React.createElement("div", {
    className: "text-base px-4 sm_px-0 text-bronze"
  }, React.createElement("div", {
    className: "".concat(classes.outletDetails, " flex flex-col sm_flex-row items-baseline mb-1")
  }, React.createElement("p", {
    className: classes.optionTitle
  }, "Configured Item #:"), React.createElement("div", {
    className: "".concat(classes.outletDetails, " flex-1 mb-1.5 w-full flex justify-between sm_mb-2.5 xl_w-8/12")
  }, React.createElement("span", null, item.sku), React.createElement("span", {
    className: "flex sm_mb-2.5 justify-end"
  }, React.createElement(DownloadsPopup, {
    product: product,
    isOutlet: true,
    installationSheetUrl: customAttributes === null || customAttributes === void 0 ? void 0 : (_customAttributes$fin = customAttributes.find(function (item) {
      return item.attribute_metadata.code == 'hf_installation_instructions';
    })) === null || _customAttributes$fin === void 0 ? void 0 : (_customAttributes$fin2 = _customAttributes$fin.entered_attribute_value) === null || _customAttributes$fin2 === void 0 ? void 0 : _customAttributes$fin2.value
  })))), React.createElement("div", {
    className: "".concat(classes.outletDetails, " flex flex-col sm_flex-row mb-3 xl_mb-0")
  }, React.createElement("span", {
    className: classes.optionTitle
  }, "Smart String: "), React.createElement("span", {
    className: "w-full xl_w-8/12 text-bronze"
  }, item.hf_smart_string)), (item === null || item === void 0 ? void 0 : item.hf_pipe_length_min) && (item === null || item === void 0 ? void 0 : item.hf_pipe_length_max) && React.createElement("div", {
    className: "".concat(classes.outletDetails, " flex flex-col sm_flex-row")
  }, React.createElement("span", {
    className: classes.optionTitle
  }, "Overall Height: "), React.createElement("span", {
    className: "w-full xl_w-8/12 text-bronze"
  }, "".concat(item === null || item === void 0 ? void 0 : item.hf_pipe_length_min, "\"  to ").concat(item === null || item === void 0 ? void 0 : item.hf_pipe_length_max, "\" ")))), React.createElement("div", {
    className: "".concat(classes.optionItemsWrapper, " mt-4")
  }, selectOptionItems)), React.createElement("div", {
    className: classes.detailsWrapper + ' sm_pl-3'
  }, React.createElement("div", {
    className: "flex justify-between mb-4 md_items-center items-start md_flex-row flex-col"
  }, React.createElement("div", {
    className: "md_mb-0 mb-4"
  }, (item === null || item === void 0 ? void 0 : (_item$price = item.price) === null || _item$price === void 0 ? void 0 : (_item$price$regularPr = _item$price.regularPrice) === null || _item$price$regularPr === void 0 ? void 0 : (_item$price$regularPr2 = _item$price$regularPr.amount) === null || _item$price$regularPr2 === void 0 ? void 0 : _item$price$regularPr2.value) > ((_item$price_range = item.price_range) === null || _item$price_range === void 0 ? void 0 : (_item$price_range$max = _item$price_range.maximum_price) === null || _item$price_range$max === void 0 ? void 0 : (_item$price_range$max2 = _item$price_range$max.final_price) === null || _item$price_range$max2 === void 0 ? void 0 : _item$price_range$max2.value) && React.createElement(React.Fragment, null, React.createElement(Price, {
    currencyCode: item === null || item === void 0 ? void 0 : (_item$price2 = item.price) === null || _item$price2 === void 0 ? void 0 : (_item$price2$regularP = _item$price2.regularPrice) === null || _item$price2$regularP === void 0 ? void 0 : (_item$price2$regularP2 = _item$price2$regularP.amount) === null || _item$price2$regularP2 === void 0 ? void 0 : _item$price2$regularP2.currency,
    value: item === null || item === void 0 ? void 0 : (_item$price3 = item.price) === null || _item$price3 === void 0 ? void 0 : (_item$price3$regularP = _item$price3.regularPrice) === null || _item$price3$regularP === void 0 ? void 0 : (_item$price3$regularP2 = _item$price3$regularP.amount) === null || _item$price3$regularP2 === void 0 ? void 0 : _item$price3$regularP2.value,
    classes: {
      root: classes.regularPriceStyle
    }
  }), React.createElement(Price, {
    currencyCode: (_item$price_range2 = item.price_range) === null || _item$price_range2 === void 0 ? void 0 : (_item$price_range2$ma = _item$price_range2.maximum_price) === null || _item$price_range2$ma === void 0 ? void 0 : (_item$price_range2$ma2 = _item$price_range2$ma.final_price) === null || _item$price_range2$ma2 === void 0 ? void 0 : _item$price_range2$ma2.currency,
    value: (_item$price_range3 = item.price_range) === null || _item$price_range3 === void 0 ? void 0 : (_item$price_range3$ma = _item$price_range3.maximum_price) === null || _item$price_range3$ma === void 0 ? void 0 : (_item$price_range3$ma2 = _item$price_range3$ma.final_price) === null || _item$price_range3$ma2 === void 0 ? void 0 : _item$price_range3$ma2.value,
    classes: {
      root: classes.priceStyle
    }
  })), (item === null || item === void 0 ? void 0 : (_item$price4 = item.price) === null || _item$price4 === void 0 ? void 0 : (_item$price4$regularP = _item$price4.regularPrice) === null || _item$price4$regularP === void 0 ? void 0 : (_item$price4$regularP2 = _item$price4$regularP.amount) === null || _item$price4$regularP2 === void 0 ? void 0 : _item$price4$regularP2.value) <= ((_item$price_range4 = item.price_range) === null || _item$price_range4 === void 0 ? void 0 : (_item$price_range4$ma = _item$price_range4.maximum_price) === null || _item$price_range4$ma === void 0 ? void 0 : (_item$price_range4$ma2 = _item$price_range4$ma.final_price) === null || _item$price_range4$ma2 === void 0 ? void 0 : _item$price_range4$ma2.value) && React.createElement(Price, {
    currencyCode: (_item$price_range5 = item.price_range) === null || _item$price_range5 === void 0 ? void 0 : (_item$price_range5$ma = _item$price_range5.maximum_price) === null || _item$price_range5$ma === void 0 ? void 0 : (_item$price_range5$ma2 = _item$price_range5$ma.final_price) === null || _item$price_range5$ma2 === void 0 ? void 0 : _item$price_range5$ma2.currency,
    value: (_item$price_range6 = item.price_range) === null || _item$price_range6 === void 0 ? void 0 : (_item$price_range6$ma = _item$price_range6.maximum_price) === null || _item$price_range6$ma === void 0 ? void 0 : (_item$price_range6$ma2 = _item$price_range6$ma.final_price) === null || _item$price_range6$ma2 === void 0 ? void 0 : _item$price_range6$ma2.value,
    classes: {
      root: classes.regularPriceStyleSingle
    }
  }))), React.createElement(Form, {
    className: "",
    onSubmit: handleAddToCart
  }, React.createElement("div", {
    className: "order-3 sm_order-2"
  }, React.createElement("div", {
    className: "flex flex-col sm_flex-row lg_items-center items-start"
  }, React.createElement("p", {
    className: classes.optionTitle
  }, "Availability:"), React.createElement("div", {
    className: "flex-1 w-full flex justify-between sm_mb-1 xl_w-8/12 2xl_w-9/12"
  }, React.createElement("span", {
    className: "text-bronze"
  }, item.saleable_qty, " Available in Outlet"), React.createElement("div", {
    className: classes.infoBtn
  }, React.createElement(InfoIcon, null), React.createElement("div", {
    className: classes.infoContent
  }, "Outlet: returns, cancellations, samples, overstocks, open box, or discontinued design or configurations. All products are 1st Quality and covered by our Lifetime Limited Warranty")))), React.createElement("div", {
    className: "flex flex-col sm_flex-row"
  }, React.createElement("p", {
    className: classes.optionTitle
  }, "Shipping:"), React.createElement("p", {
    className: "flex-1 w-full flex justify-between sm_mb-1 xl_w-8/12 2xl_w-9/12 text-bronze"
  }, "Ships Next Business Day"))), React.createElement("div", {
    className: "order-2 py-10 sm_order-3 "
  }, React.createElement(QuantityFields, {
    min: 1,
    message: errors.get('quantity')
  })), React.createElement(FormError, {
    classes: {
      root: classes.formErrors
    },
    errors: errors.get('outletForm') || []
  }), React.createElement("div", {
    className: "order-4 mt-2"
  }, React.createElement(Button, {
    priority: "low",
    fullWidth: true,
    type: "submit",
    "aria-label": "Add to cart"
  }, "ADD TO CART"), React.createElement("div", {
    className: "mt-10"
  }, React.createElement("p", {
    className: "break-normal text-red text-xs mb-5 sm_mb-0"
  }, "All Outlet Items are Final Sale. All Final Sale Items cannot be returned for exchange, credit, or refunds."))))));
};

export default AvailableOutletItem;